import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { setSelectedProject } from 'features/projects';
import { Plus } from 'lucide-react';
import { COUNTRY_OPTIONS } from 'shared/config/countries';
import { useAppDispatch } from 'shared/hooks/redux';
import PhoneInput, { isValidPhoneForCountry, parsePhoneDigits } from 'shared/ui/phone-input';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import { useCreateMasterMutation } from '../api/master.api';
import {
  CountryCode,
  CreateMasterPayload,
  CreateMasterSubProjectInput,
  FEATURE_FLAG_KEYS,
  FEATURE_FLAG_LABELS,
  flagToUi,
  ProjectFeatureFlags,
} from '../api/types';
import { PROMPT_TEMPLATES } from '../model/promptTemplates';
import FeatureToggleGroup, { DEFAULT_FEATURE_FLAGS } from '../ui/FeatureToggleGroup';
import Stepper, { StepDescriptor } from '../ui/Stepper';
import SubProjectCard, { SubProjectDraft } from '../ui/SubProjectCard';

const STEPS: ReadonlyArray<StepDescriptor> = [
  { id: 'master', label: 'Inmobiliaria' },
  { id: 'projects', label: 'Proyectos' },
  { id: 'review', label: 'Revisión' },
];

interface MasterDraft extends ProjectFeatureFlags {
  name: string;
  phoneNumber: string;
  countryCode: CountryCode;
  commonPrompt: string;
  pusherIdentification: string;
}

const slugify = (input: string): string =>
  input
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const pusherFromName = (name: string): string => {
  const slug = slugify(name);
  return slug ? `${slug}_chat` : '';
};

const initialMaster = (): MasterDraft => ({
  name: '',
  phoneNumber: '',
  countryCode: 'CL',
  commonPrompt: '',
  pusherIdentification: '',
  ...DEFAULT_FEATURE_FLAGS,
});

const buildSubProjectDraft = (defaults: ProjectFeatureFlags): SubProjectDraft => {
  const [firstTemplate] = PROMPT_TEMPLATES;
  return {
    uid: `sub-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: '',
    promptTypeId: firstTemplate?.id ?? '',
    prompt: firstTemplate?.prompt ?? '',
    ...defaults,
  };
};

const masterTogglesFromDraft = (draft: MasterDraft): ProjectFeatureFlags =>
  FEATURE_FLAG_KEYS.reduce((acc, key) => {
    acc[key] = draft[key];
    return acc;
  }, {} as ProjectFeatureFlags);

const extractErrorMessage = (e: unknown): string => {
  if (e instanceof Error) return e.message;
  if (typeof e === 'object' && e !== null && 'data' in e) {
    const { data } = e as { data: unknown };
    if (typeof data === 'string') return data;
  }
  return 'No se pudo crear la inmobiliaria. Intenta nuevamente.';
};

const subToggleOverrides = (draft: SubProjectDraft, master: ProjectFeatureFlags): Partial<ProjectFeatureFlags> =>
  FEATURE_FLAG_KEYS.reduce((acc, key) => {
    if (draft[key] !== master[key]) acc[key] = draft[key];
    return acc;
  }, {} as Partial<ProjectFeatureFlags>);

const CreateMasterPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [createMaster, { isLoading }] = useCreateMasterMutation();

  const [step, setStep] = useState(0);
  const [master, setMaster] = useState<MasterDraft>(initialMaster);
  const [pusherTouched, setPusherTouched] = useState(false);
  const [subProjects, setSubProjects] = useState<SubProjectDraft[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleBack = () => {
    if (step === 0) {
      void navigate('/project');
      return;
    }
    setStep((currentStep) => Math.max(0, currentStep - 1));
  };

  const masterToggles = useMemo(() => masterTogglesFromDraft(master), [master]);
  const parsedPhone = parsePhoneDigits(master.phoneNumber, master.countryCode);
  const isPhoneValid = isValidPhoneForCountry(master.phoneNumber, parsedPhone.country);

  const masterStepValid =
    master.name.trim().length > 0 && isPhoneValid && COUNTRY_OPTIONS.some((c) => c.code === master.countryCode);

  const projectsStepValid = subProjects.every((sp) => sp.name.trim().length > 0);

  const updateMaster = <K extends keyof MasterDraft>(key: K, value: MasterDraft[K]) => {
    setMaster((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'name' && !pusherTouched) {
        next.pusherIdentification = pusherFromName(value as string);
      }
      return next;
    });
  };

  const handlePusherChange = (value: string) => {
    setPusherTouched(true);
    setMaster((prev) => ({ ...prev, pusherIdentification: value }));
  };

  const handleMasterFeatures = (next: ProjectFeatureFlags) => {
    setMaster((prev) => ({ ...prev, ...next }));
  };

  const handleAddSubProject = () => {
    setSubProjects((prev) => [...prev, buildSubProjectDraft(masterToggles)]);
  };

  const handleSubProjectChange = (index: number, next: SubProjectDraft) => {
    setSubProjects((prev) => prev.map((sp, i) => (i === index ? next : sp)));
  };

  const handleSubProjectDuplicate = (index: number) => {
    setSubProjects((prev) => {
      const target = prev[index];
      if (!target) return prev;
      const copy: SubProjectDraft = {
        ...target,
        uid: `sub-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: `${target.name} (copia)`.trim(),
      };
      const next = [...prev];
      next.splice(index + 1, 0, copy);
      return next;
    });
  };

  const handleSubProjectRemove = (index: number) => {
    setSubProjects((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setErrorMessage(null);
    const payload: CreateMasterPayload = {
      master: {
        name: master.name.trim(),
        phoneNumber: master.phoneNumber.trim(),
        countryCode: master.countryCode,
        commonPrompt: master.commonPrompt,
        pusherIdentification: master.pusherIdentification,
        ...masterToggles,
      },
      subProjects: subProjects.map<CreateMasterSubProjectInput>((sp) => ({
        name: sp.name.trim(),
        prompt: sp.prompt,
        promptTypeId: sp.promptTypeId || undefined,
        ...subToggleOverrides(sp, masterToggles),
      })),
    };
    try {
      const created = await createMaster(payload).unwrap();
      dispatch(setSelectedProject(created.id));
      void navigate(`/project/${created.id}/config`);
    } catch (e) {
      setErrorMessage(extractErrorMessage(e));
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header>
        <h1 className="font-display text-2xl font-bold">Crear Inmobiliaria</h1>
        <p className="text-sm text-text-muted">
          Define la inmobiliaria y sus proyectos. Todo se crea en una sola operación.
        </p>
      </header>

      <Stepper steps={STEPS} currentIndex={step} />

      {step === 0 && (
        <section className="space-y-6 rounded-md border border-border p-6">
          <h2 className="font-display text-lg font-semibold">Datos generales</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="master-name">Nombre</Label>
              <Input
                id="master-name"
                value={master.name}
                onChange={(e) => updateMaster('name', e.target.value)}
                placeholder="Ej. Inmobiliaria Aurora"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="master-phone">Teléfono</Label>
              <PhoneInput
                inputId="master-phone"
                value={master.phoneNumber}
                onChange={(phoneNumber) => updateMaster('phoneNumber', phoneNumber)}
                defaultCountry={master.countryCode}
              />
              {!isPhoneValid && parsedPhone.local.length > 0 && (
                <p className="text-xs text-destructive">Número de teléfono inválido para {parsedPhone.country}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="master-country">País</Label>
              <Select
                value={master.countryCode}
                onValueChange={(value) => updateMaster('countryCode', value as CountryCode)}
              >
                <SelectTrigger id="master-country">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.code} value={opt.code}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="master-pusher">Pusher identification</Label>
              <Input
                id="master-pusher"
                value={master.pusherIdentification}
                onChange={(e) => handlePusherChange(e.target.value)}
                placeholder="se genera desde el nombre"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="master-common-prompt">Prompt común</Label>
            <Textarea
              id="master-common-prompt"
              value={master.commonPrompt}
              onChange={(e) => updateMaster('commonPrompt', e.target.value)}
              rows={5}
              placeholder="Texto base que se aplica a todos los proyectos."
            />
          </div>
          <FeatureToggleGroup value={masterToggles} onChange={handleMasterFeatures} />
        </section>
      )}

      {step === 1 && (
        <section className="space-y-4 rounded-md border border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display text-lg font-semibold">Proyectos</h2>
              <p className="text-sm text-text-muted">
                {subProjects.length} proyecto
                {subProjects.length === 1 ? '' : 's'}. Pueden quedar sin proyectos.
              </p>
            </div>
            <Button type="button" onClick={handleAddSubProject} className="gap-2">
              <Plus className="size-4" />
              Agregar proyecto
            </Button>
          </div>
          <div className="space-y-3">
            {subProjects.map((sp, index) => (
              <SubProjectCard
                key={sp.uid}
                index={index}
                draft={sp}
                onChange={(next) => handleSubProjectChange(index, next)}
                onDuplicate={() => handleSubProjectDuplicate(index)}
                onRemove={() => handleSubProjectRemove(index)}
                defaultExpanded={index === subProjects.length - 1}
              />
            ))}
            {subProjects.length === 0 && (
              <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-text-muted">
                Aún no hay proyectos. Puedes agregar uno o continuar sin proyectos.
              </p>
            )}
          </div>
        </section>
      )}

      {step === 2 && (
        <section className="space-y-4 rounded-md border border-border p-6">
          <h2 className="font-display text-lg font-semibold">Revisión</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1 text-sm">
              <p className="text-xs uppercase tracking-wider text-text-muted">Nombre</p>
              <p className="font-medium">{master.name}</p>
            </div>
            <div className="space-y-1 text-sm">
              <p className="text-xs uppercase tracking-wider text-text-muted">Teléfono</p>
              <p className="font-medium">{master.phoneNumber}</p>
            </div>
            <div className="space-y-1 text-sm">
              <p className="text-xs uppercase tracking-wider text-text-muted">País</p>
              <p className="font-medium">{master.countryCode}</p>
            </div>
            <div className="space-y-1 text-sm">
              <p className="text-xs uppercase tracking-wider text-text-muted">Pusher</p>
              <p className="font-medium">{master.pusherIdentification || '—'}</p>
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-text-muted mb-2">Características del master</p>
            <ul className="grid grid-cols-1 gap-1 text-sm md:grid-cols-2">
              {FEATURE_FLAG_KEYS.map((key) => {
                const uiValue = flagToUi(key, masterToggles[key]);
                return (
                  <li key={key} className="flex justify-between gap-2 rounded border border-border px-2 py-1">
                    <span>{FEATURE_FLAG_LABELS[key].title}</span>
                    <span className={uiValue ? 'text-brand-turquoise' : 'text-text-muted'}>
                      {uiValue ? 'Sí' : 'No'}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-text-muted mb-2">Proyectos ({subProjects.length})</p>
            {subProjects.length === 0 ? (
              <p className="text-sm text-text-muted">Sin proyectos. Podrás agregarlos luego.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {subProjects.map((sp, index) => (
                  <li key={sp.uid} className="rounded border border-border p-3">
                    <p className="font-medium">
                      {index + 1}. {sp.name}
                    </p>
                    <p className="text-xs text-text-muted">
                      Tipo: {sp.promptTypeId || '—'} · {FEATURE_FLAG_KEYS.filter((k) => flagToUi(k, sp[k])).length}{' '}
                      features activas
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {errorMessage && (
            <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
              {errorMessage}
            </p>
          )}
        </section>
      )}

      <div className="flex items-center justify-between">
        <Button type="button" variant="outline" onClick={handleBack} disabled={isLoading}>
          Volver
        </Button>
        {step < STEPS.length - 1 ? (
          <Button
            type="button"
            onClick={() => setStep((s) => s + 1)}
            disabled={(step === 0 && !masterStepValid) || (step === 1 && !projectsStepValid)}
          >
            Siguiente
          </Button>
        ) : (
          <Button
            type="button"
            onClick={() => {
              void handleSubmit();
            }}
            disabled={isLoading || !masterStepValid}
          >
            {isLoading ? 'Creando…' : 'Crear inmobiliaria'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CreateMasterPage;
